import React, { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { translations, RTL_LANGS } from "@/i18n";
import { setDisplayCurrency } from "@/lib/format";
import { homeCurrency } from "@/lib/fx";

const AppContext = createContext(null);

const IDLE_LIMIT_MS = 30 * 60 * 1000;
const IDLE_WARN_MS = 28 * 60 * 1000;

export const AppProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lang, setLang] = useState(localStorage.getItem("tharwy_lang") || "en");
  const [contrast, setContrast] = useState(localStorage.getItem("tharwy_contrast") === "high");
  const [theme, setTheme] = useState(localStorage.getItem("tharwy_theme") || "light");
  const [displayCcy, setDisplayCcy] = useState(localStorage.getItem("tharwy_display_ccy") || "USD");
  const signedIn = useRef(false);
  signedIn.current = !!user;

  const applyDisplayCcy = useCallback((code) => {
    setDisplayCcy(code);
    localStorage.setItem("tharwy_display_ccy", code);
    setDisplayCurrency(code);
  }, []);

  // No explicit choice yet? Show the client their own country's currency.
  const applyHomeCcy = useCallback((u) => {
    if (localStorage.getItem("tharwy_display_ccy")) return;
    const code = homeCurrency(u);
    setDisplayCcy(code);
    setDisplayCurrency(code);
  }, []);

  const resetDisplayCcy = useCallback((u) => {
    localStorage.removeItem("tharwy_display_ccy");
    const code = homeCurrency(u);
    setDisplayCcy(code);
    setDisplayCurrency(code);
  }, []);

  const t = useCallback((key) => translations[lang]?.[key] || translations.en[key] || key, [lang]);

  const applyLang = useCallback((l) => {
    setLang(l);
    localStorage.setItem("tharwy_lang", l);
    document.documentElement.setAttribute("dir", RTL_LANGS.includes(l) ? "rtl" : "ltr");
    document.documentElement.setAttribute("lang", l);
    // Remember it on the account too, so emails go out in the language they read the app in.
    if (signedIn.current) {
      api.patch("/auth/preferences", { language: l })
        .then(({ data }) => setUser((u) => (u ? { ...u, language: data.language } : u)))
        .catch(() => {});
    }
  }, []);

  const applyContrast = useCallback((high) => {
    setContrast(high);
    localStorage.setItem("tharwy_contrast", high ? "high" : "normal");
    if (high) document.documentElement.setAttribute("data-contrast", "high");
    else document.documentElement.removeAttribute("data-contrast");
  }, []);

  const applyTheme = useCallback((next) => {
    setTheme(next);
    localStorage.setItem("tharwy_theme", next);
    document.documentElement.classList.toggle("dark", next === "dark");
    document.documentElement.style.colorScheme = next;
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      applyHomeCcy(data);
      return data;
    } catch (e) {
      setUser(null);
      return null;
    }
  }, [applyHomeCcy]);

  useEffect(() => {
    applyLang(lang);
    applyContrast(contrast);
    applyTheme(theme);
    (async () => {
      // Session lives in an httpOnly cookie — always try to resolve the user;
      // the browser attaches the cookie automatically. (Legacy localStorage
      // tokens, if any, are still honoured by the axios request interceptor.)
      await refreshUser();
      setLoading(false);
    })();
    // eslint-disable-next-line
  }, []);

  const login = useCallback(async (email, password, mfaCode) => {
    const payload = { email, password };
    if (mfaCode) payload.mfa_code = mfaCode;
    const { data } = await api.post("/auth/login", payload);
    setUser(data.user);
    applyHomeCcy(data.user);
    return data.user;
  }, [applyHomeCcy]);

  const register = useCallback(async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setUser(data.user);
    applyHomeCcy(data.user);
    return data.user;
  }, [applyHomeCcy]);

  // Simulated social sign-in for Outlook (Microsoft) & Apple ID (demo accounts).
  const socialDemo = useCallback(async (provider) => {
    const { data } = await api.post("/auth/oauth/demo", { provider });
    setUser(data.user);
    return data.user;
  }, []);

  // Update personal contact details (email, phone, WhatsApp, address, country, base currency).
  const updateContact = useCallback(async (payload) => {
    const { data } = await api.patch("/auth/contact", payload);
    setUser(data);
    applyHomeCcy(data);
    return data;
  }, [applyHomeCcy]);

  // Save the post-signup questionnaire and mark onboarding complete.
  const submitOnboarding = useCallback(async (payload) => {
    const { data } = await api.post("/auth/onboarding", payload);
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    // Clear the server-side httpOnly cookie. Auth is cookie-only now (no token
    // is stored client-side), so there is nothing to clear from localStorage.
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.error("Logout request failed", e);
    }
    setUser(null);
  }, []);

  // Idle session timeout: 30 minutes of inactivity signs the user out, with a
  // warning two minutes before. Required for KYC / regulated sessions.
  useEffect(() => {
    if (!user) return undefined;
    let warnT, outT;
    const arm = () => {
      clearTimeout(warnT); clearTimeout(outT);
      warnT = setTimeout(() => {
        toast.warning("Still there?", { description: "You'll be signed out in 2 minutes for your security." });
      }, IDLE_WARN_MS);
      outT = setTimeout(async () => {
        await logout();
        toast.error("Signed out", { description: "Your session timed out after 30 minutes of inactivity." });
      }, IDLE_LIMIT_MS);
    };
    const events = ["click", "keydown", "mousemove", "scroll", "touchstart"];
    events.forEach((e) => window.addEventListener(e, arm, { passive: true }));
    arm();
    return () => {
      clearTimeout(warnT); clearTimeout(outT);
      events.forEach((e) => window.removeEventListener(e, arm));
    };
  }, [user, logout]);

  // One stable context object: a fresh one on every render re-renders every screen.
  const value = useMemo(() => ({
    user, setUser, loading, login, register, logout, refreshUser, socialDemo, updateContact,
    submitOnboarding,
    lang, setLang: applyLang, t, contrast, setContrast: applyContrast,
    theme, setTheme: applyTheme, toggleTheme: () => applyTheme(theme === "dark" ? "light" : "dark"),
    displayCcy, setDisplayCcy: applyDisplayCcy, resetDisplayCcy,
    homeCcy: homeCurrency(user),
    ccyPinned: !!localStorage.getItem("tharwy_display_ccy"),
  }), [user, loading, login, register, logout, refreshUser, socialDemo, updateContact,
       submitOnboarding, lang, applyLang, t, contrast, applyContrast, theme, applyTheme,
       displayCcy, applyDisplayCcy, resetDisplayCcy]);

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useApp = () => useContext(AppContext);
